import { Component, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { And, Not, RuleInterface, StringEquals, StringListContains } from '@vendasta/automata';
import { SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { User } from '@vendasta/iam';
import { TaskStatus, TaskStatusesSdkService } from '@vendasta/task-manager';
import { combineLatest, Observable, of } from 'rxjs';
import { debounceTime, map, shareReplay, startWith, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AutomationReplacementChipsDataService } from '../../../../../common/components';
import { TaskService } from '../../../../../data-services/task.service';
import { UsersService } from '../../../../../data-services/users.service';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import { TriggerBaseRules } from '../../../common/decorators/trigger-rules.decorator';
import { TaskManagerPersonaType } from '../task-status-changed.service';

@Component({
  template: '',
  standalone: false,
})
export abstract class TaskStatusChangedPanelComponent extends CommonTriggerPanelComponent implements OnInit {
  abstract isProjectType: boolean;
  abstract taskPersonaType: TaskManagerPersonaType;
  abstract assigneeFilterDescriptionKey: string;
  abstract subtitleKey: string;
  abstract nameFilterTitle: string;
  abstract service: AutomationReplacementChipsDataService<User>;

  form: UntypedFormGroup;

  taskStatusOptions$: Observable<SelectInputOptionInterface[]>;
  taskStatusControl: UntypedFormControl = new UntypedFormControl('');

  taskNameOptions$: Observable<string[]>;
  taskNameControl: UntypedFormControl = new UntypedFormControl('');

  taskAssigneeFormControl: UntypedFormControl = new UntypedFormControl('');

  isLoading = true;

  isNonPartnerContext$ = this.automationService.isNonPartnerAutomation().pipe(
    take(1),
    tap((isNonPartnerAutomation) => {
      if (!isNonPartnerAutomation) {
        this.taskStatusControl.setValidators(Validators.required);
      }
    }),
  );

  nonPartnerContextStatuses: TaskStatus[] = [
    { name: '' },
    { name: 'Open' },
    { name: 'In Progress' },
    { name: 'Waiting on Customer' },
    { name: 'Completed' },
  ] as TaskStatus[];
  statuses: TaskStatus[] = [];

  constructor(
    protected readonly partnerId$: Observable<string>,
    protected readonly taskStatusesSdkService: TaskStatusesSdkService,
    protected readonly usersService: UsersService,
    protected readonly taskService: TaskService,
    protected readonly automationService: AutomationViewService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.triggerChoices?.rules?.length > 0) {
      const and = And.fromRuleInterface(this.triggerChoices.rules[0]);
      const personaFromRule = and.listValuesByVariablePath('{.task_data.persona_type}');
      if (!!personaFromRule && personaFromRule.length > 0) {
        this.taskPersonaType = personaFromRule[0];
      }
      const statusFromRule = and.listValuesByVariablePath('{.status}');
      if (!!statusFromRule && statusFromRule.length > 0) {
        // the status value is a list
        this.taskStatusControl.setValue(statusFromRule[0][0]);
      }
      const taskNameFromRule = and.listValuesByVariablePath('{.task_data.name}');
      if (!!taskNameFromRule && taskNameFromRule.length > 0) {
        this.taskNameControl.setValue(taskNameFromRule[0]);
      }
      const taskAssigneeFromRule = and.listValuesByVariablePath('{.task_data.assignees}');
      if (!!taskAssigneeFromRule && taskAssigneeFromRule.length > 0) {
        // the assignee value is a list
        this.taskAssigneeFormControl.setValue(taskAssigneeFromRule[0][0]);
      }
    }

    this.form = new UntypedFormGroup({});
    this.form.addControl('status', this.taskStatusControl);
    this.form.addControl('task', this.taskNameControl);
    this.form.addControl('user_id', this.taskAssigneeFormControl);

    this.taskStatusOptions$ = combineLatest([this.isNonPartnerContext$, this.partnerId$]).pipe(
      switchMap(([disablePartnerSpecificStatusSelection, partnerId]) => {
        if (disablePartnerSpecificStatusSelection) {
          // use default task statuses across all partners
          return of({ statuses: this.nonPartnerContextStatuses });
        }
        if (this.statuses.length > 0) {
          // use child component statues if they are defined
          return of({ statuses: this.statuses });
        }
        return this.taskStatusesSdkService.listTaskStatuses(partnerId, '', 50);
      }),
      take(1),
      shareReplay(1),
      map((statusList) =>
        (statusList?.statuses || []).map((statuses) => {
          return this.statusListToOption(statuses);
        }),
      ),
      tap(() => this.savingEnabled$.next(true)),
      tap(() => {
        this.isLoading = false;
      }),
      map((options) => {
        return options.map((option) => {
          if (option.value === this.taskStatusControl.value) {
            option.selected = true;
          }
          return option;
        });
      }),
    );

    this.taskNameOptions$ = this.taskNameControl.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      withLatestFrom(this.partnerId$, this.isNonPartnerContext$),
      switchMap(([value, partnerId, disablePartnerSpecificStatusSelection]) => {
        if (disablePartnerSpecificStatusSelection) {
          return of([]);
        }

        let searchTerm = '';
        if (typeof value === 'string') {
          searchTerm = value;
        }

        return this.taskService.searchTasks(partnerId, this.taskPersonaType, searchTerm, this.isProjectType);
      }),
      map((tasks) => (tasks || []).map((task) => task.title)),
      map((tasks) => {
        // filter out unique task names for user selection
        return tasks.filter((val, index, arr) => arr.indexOf(val) === index);
      }),
    );
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  isFormValid(): boolean {
    return !this.form.invalid;
  }

  @TriggerBaseRules
  getBaseRules(): RuleInterface[] {
    const rules: any = [new StringEquals('{.task_data.persona_type}', this.taskPersonaType, '')];
    if (this.isProjectType) {
      rules.push(new StringEquals('{.task_data.task_type}', 'Project'));
    } else {
      rules.push(new Not(new StringEquals('{.task_data.task_type}', 'Project')));
    }

    if (this.taskStatusControl.value) {
      rules.push(new StringListContains('{.status}', [this.taskStatusControl.value]));
    }
    if (this.taskNameControl.value) {
      rules.push(new StringEquals('{.task_data.name}', this.taskNameControl.value, ''));
    }

    if (this.taskAssigneeFormControl.value) {
      rules.push(new StringListContains('{.task_data.assignees}', [this.taskAssigneeFormControl.value], ''));
    }

    return [new And(rules, '').toRuleInterface()];
  }

  fillRuleValues(rules: RuleInterface[]): RuleInterface[] {
    if (!this.taskStatusControl.value) {
      return rules;
    }
    return this.getBaseRules();
  }

  statusListToOption(taskStatus: TaskStatus): SelectInputOptionInterface {
    return {
      label: taskStatus.name,
      value: taskStatus.name,
    };
  }
}
